import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Breadcrumbs from '../../Common/Breadcrumbs';
import PageHeader from '../../Common/PageHeader';
import { uploadNewsArticleImage } from '../../api/newsArticle/uploadNewsArticleImage';
import { addNewsArticle } from '../../api/newsArticle/addNewsArticle';
import { updateNewsArticle } from '../../api/newsArticle/updateNewsArticle';
import { NewsArticle, NewsArticleAddPayload } from '../../api/newsArticle/types';
import { getNewsArticle } from '../../api/newsArticle/getNewsArticle';
import { getImageUrl } from '../../utils/imageUrl';

const NewsArticleDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [news, setNews] = useState<NewsArticle>({ title: '', description: '', image: '', companyName: '', externalLink: '', uploadedDate: '' });
  const [editMode, setEditMode] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [initialNews, setInitialNews] = useState<NewsArticle | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{[key: string]: string}>({});
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Validation functions
  const validateField = (field: string, value: string): string => {
    const trimmedValue = value.trim();
    
    if (field === 'title') {
      if (!trimmedValue) {
        return 'Title is required';
      }
      if (value !== trimmedValue) {
        return 'Title cannot contain only spaces';
      }
    } else if (['description', 'companyName', 'externalLink'].includes(field)) {
      if (value && value !== trimmedValue) {
        return `${field.charAt(0).toUpperCase() + field.slice(1)} cannot contain only spaces`;
      }
    }
    
    return '';
  };

  const validateForm = (): boolean => {
    const errors: {[key: string]: string} = {};
    
    // Validate title (required)
    const titleError = validateField('title', news.title);
    if (titleError) errors.title = titleError;
    
    // Validate optional fields if they have content
    const descriptionError = validateField('description', news.description || '');
    if (descriptionError) errors.description = descriptionError;
    
    const companyNameError = validateField('companyName', news.companyName || '');
    if (companyNameError) errors.companyName = companyNameError;
    
    const externalLinkError = validateField('externalLink', news.externalLink || '');
    if (externalLinkError) errors.externalLink = externalLinkError;
    
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  useEffect(() => {
    const fetchNews = async () => {
      if (id && !location.state?.news) {
        try {
          const article = await getNewsArticle(id);
          setNews(article);
          setInitialNews(article);
          if (article.image) {
            setImagePreview(getImageUrl(article.image));
          }
          setEditMode(true);
        } catch (err) {
          setError('Failed to fetch news article.');
        }
      }
    };

    if (location.state?.news) {
      setNews(location.state.news);
      setInitialNews(location.state.news);
      if (location.state.news.image) {
        setImagePreview(getImageUrl(location.state.news.image));
      }
      if (location.state?.edit) {
        setEditMode(true);
      }
    } else if (id) {
      fetchNews();
    } else {
      setNews({ title: '', description: '', image: '', companyName: '', externalLink: '', uploadedDate: '' });
      setEditMode(true);
    }
  }, [id, location.state]);

  const handleChange = (field: keyof NewsArticle, value: any) => {
    setNews((prev: any) => ({ ...prev, [field]: value }));
    
    // Clear error for this field when user starts typing
    if (fieldErrors[field]) {
      setFieldErrors(prev => ({ ...prev, [field]: '' }));
    }
    
    // Validate field in real-time
    const error = validateField(field, value);
    if (error) {
      setFieldErrors(prev => ({ ...prev, [field]: error }));
    }
  };

  const handleImageUpload = async (file: File) => {
    setIsUploading(true);
    setUploadError(null);
    try {
      const imageUrl = await uploadNewsArticleImage(file);
      setNews((prev: any) => ({ ...prev, image: imageUrl }));
      setImagePreview(URL.createObjectURL(file));
    } catch (err) {
      setUploadError('Image upload failed. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const isFormValid = news.title.trim().length > 0 && Object.keys(fieldErrors).length === 0;
  const isChanged = !initialNews || Object.keys(news).some(key => (news as any)[key] !== (initialNews as any)?.[key]);

  const handleSave = async () => {
    if (!validateForm()) {
      setError('Please fix the validation errors before saving.');
      return;
    }
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      if (id) {
        await updateNewsArticle(id, {
          title: news.title.trim(),
          description: news.description?.trim() || '',
          image: news.image,
          companyName: news.companyName?.trim() || '',
          externalLink: news.externalLink?.trim() || '',
          uploadedDate: news.uploadedDate,
        });
        setSuccess('News article updated successfully.');
      } else {
        await addNewsArticle({
          title: news.title.trim(),
          description: news.description?.trim() || '',
          image: news.image,
          companyName: news.companyName?.trim() || '',
          externalLink: news.externalLink?.trim() || '',
          uploadedDate: news.uploadedDate,
        });
        setSuccess('News article created successfully.');
      }
      setEditMode(false);
      setTimeout(() => navigate('/news-articles', { state: { activeId: id } }), 1000);
    } catch (err: any) {
      setError('Failed to save news article.');
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    navigate('/news-articles', { state: { activeId: news?._id } });
  };

  const handlePasteUrl = async () => {
    try {
      const text = await navigator.clipboard.readText();
      setNews((prev: any) => ({ ...prev, externalLink: text }));
    } catch (err) {
      // Optionally show error
    }
  };

  if (!news) {
    return <div className="container-fluid" style={{ marginTop: '20px' }}><h2>Loading...</h2></div>;
  }

  return (
    <div className="container-fluid" style={{ marginTop: '20px' }}>
      <Breadcrumbs items={[
        { label: 'News Articles', onClick: () => navigate('/news-articles') },
        { label: news.title || 'New Article', active: true }
      ]} />
      <PageHeader
        breadcrumb={null}
        title={editMode ? (id ? 'Edit News Article' : 'Add News Article') : 'News Article Details'}
        addNewLabel={undefined}
      />
      <div className="card" style={{ maxWidth: 600, margin: '0 auto', padding: 24 }}>
        <div className="mb-3">
          <label className="form-label">Title <span style={{ color: 'red' }}>*</span></label>
          <input
            className={`form-control ${fieldErrors.title ? 'is-invalid' : ''}`}
            value={news.title}
            disabled={!editMode}
            onChange={e => handleChange('title', e.target.value)}
          />
          {fieldErrors.title && <div className="invalid-feedback">{fieldErrors.title}</div>}
        </div>
        <div className="mb-3">
          <label className="form-label">Description</label>
          <textarea
            className={`form-control ${fieldErrors.description ? 'is-invalid' : ''}`}
            value={news.description || ''}
            disabled={!editMode}
            onChange={e => handleChange('description', e.target.value)}
          />
          {fieldErrors.description && <div className="invalid-feedback">{fieldErrors.description}</div>}
        </div>
        <div className="mb-3">
          <label className="form-label">Company Name</label>
          <input
            className={`form-control ${fieldErrors.companyName ? 'is-invalid' : ''}`}
            value={news.companyName || ''}
            disabled={!editMode}
            onChange={e => handleChange('companyName', e.target.value)}
          />
          {fieldErrors.companyName && <div className="invalid-feedback">{fieldErrors.companyName}</div>}
        </div>
        <div className="mb-3">
          <label className="form-label">Uploaded Date</label>
          <input
            type="date"
            className="form-control"
            value={news.uploadedDate ? news.uploadedDate.substring(0, 10) : ''}
            disabled={!editMode}
            onChange={e => handleChange('uploadedDate', e.target.value)}
          />
        </div>
        <div className="mb-3">
          <label className="form-label">News Image</label>
          <input
            type="file"
            className="form-control"
            accept="image/*"
            onChange={e => {
              const file = (e.target as HTMLInputElement).files?.[0];
              if (file) handleImageUpload(file);
            }}
            ref={fileInputRef}
            style={{ display: 'none' }}
            disabled={!editMode}
          />
          <div className="mt-3">
            <div
              style={{
                position: 'relative',
                width: 300,
                height: 200,
                borderRadius: 8,
                overflow: 'hidden',
                border: '1px solid #dee2e6',
                background: '#f8f9fa',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: editMode ? 'pointer' : 'default'
              }}
              onClick={() => editMode && fileInputRef.current?.click()}
            >
              {imagePreview ? (
                <>
                  <img
                    src={imagePreview}
                    alt="Preview"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      borderRadius: 8,
                      opacity: isUploading ? 0.5 : 1
                    }}
                  />
                  {isUploading && (
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: 'rgba(255,255,255,0.7)',
                      zIndex: 3
                    }}>
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Uploading...</span>
                      </div>
                    </div>
                  )}
                  {editMode && !isUploading && (
                    <button
                      type="button"
                      onClick={e => {
                        e.stopPropagation();
                        fileInputRef.current?.click();
                      }}
                      style={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        background: 'rgba(255,255,255,0.8)',
                        border: 'none',
                        borderRadius: '50%',
                        padding: 4,
                        cursor: 'pointer',
                        zIndex: 2,
                      }}
                      title="Edit Image"
                    >
                      <i className="bi bi-pencil" style={{ color: '#007bff', fontSize: 18 }} />
                    </button>
                  )}
                </>
              ) : (
                <div style={{ textAlign: 'center' }}>
                  <i className="bi bi-cloud-upload display-4"></i>
                  <p className="mt-2 mb-0">
                    {editMode ? 'Click to upload' : 'No image'}
                  </p>
                  {isUploading && (
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: 'rgba(255,255,255,0.7)',
                      zIndex: 3
                    }}>
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Uploading...</span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          {isUploading && <p>Uploading...</p>}
          {uploadError && <p style={{ color: 'red' }}>{uploadError}</p>}
        </div>
        <div className="mb-3">
          <label className="form-label">External Link</label>
          <div className="input-group">
            <input
              className={`form-control ${fieldErrors.externalLink ? 'is-invalid' : ''}`}
              value={news.externalLink || ''}
              disabled={!editMode}
              onChange={e => handleChange('externalLink', e.target.value)}
              placeholder="Paste the news article URL here"
            />
            {editMode && (
              <button className="btn btn-outline-secondary" type="button" onClick={handlePasteUrl} title="Paste from clipboard">
                <i className="bi bi-clipboard-plus"></i>
              </button>
            )}
          </div>
          {fieldErrors.externalLink && <div className="invalid-feedback">{fieldErrors.externalLink}</div>}
        </div>
        {error && <div className="alert alert-danger mt-2">{error}</div>}
        {success && <div className="alert alert-success mt-2">{success}</div>}
        <div className="d-flex gap-2 mt-4">
          {editMode ? (
            <>
              <button className="btn btn-primary" onClick={handleSave} disabled={!isFormValid || !isChanged || saving}>{id ? (saving ? 'Updating...' : 'Update Article') : (saving ? 'Creating...' : 'Create Article')}</button>
              <button className="btn btn-secondary" onClick={handleBack} disabled={saving}>Cancel</button>
            </>
          ) : (
            <>
              <button className="btn btn-primary" onClick={() => setEditMode(true)}>Edit</button>
              <button className="btn btn-outline-secondary" onClick={handleBack}>Back</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default NewsArticleDetails;
